import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { strings } from "./strings";

// The checkout in English, Spanish or Brazilian Portuguese. Only the order steps (/order/type to
// /order/payment and its confirmation) and the quote request (/quote/details,
// /quote/options, /quote/contact) follow the chosen language; every other
// page, and every value written to the order or the quote, stays as it was.
//
// The language comes from ?lang=en|es|pt, then the one chosen before (kept in
// localStorage), then the browser's own language. The switch in the checkout
// header changes it.

export const LANGS = ["en", "es", "pt"];

// The languages with their own text, and the locale Intl writes each one's dates in.
const LOCALES = { es: "es", pt: "pt-BR" };
export const isTranslated = (lang) => Boolean(LOCALES[lang]);
export const intlLocale = (lang) => LOCALES[lang] || "en-US";
const STORAGE_KEY = "checkoutLang";

export const isCheckoutPath = (pathname) => /^\/(order\/(type|documents|options|contact|payment)|quote\/(details|options|contact))\/?$/.test(pathname || "");

// The language of the page being drawn, for helpers that are not components.
// Always "en" outside the checkout.
let current = "en";
export const currentLang = () => current;

const readStored = () => {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return LANGS.includes(value) ? value : null;
  } catch (e) {
    return null;
  }
};

const store = (lang) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, lang);
  } catch (e) {
    // Private windows and blocked storage: the choice lasts for this visit.
  }
};

const fromUrl = (search) => {
  try {
    const value = new URLSearchParams(search || "").get("lang");
    return LANGS.includes(value) ? value : null;
  } catch (e) {
    return null;
  }
};

// "pt-BR" -> "pt". Anything we don't have goes to English.
export const langOfTag = (tag) => {
  const value = String(tag || "").toLowerCase();
  if (value.startsWith("es")) {
    return "es";
  }
  return value.startsWith("pt") ? "pt" : "en";
};

const fromBrowser = () => {
  try {
    return langOfTag(navigator.language);
  } catch (e) {
    return "en";
  }
};

// t("key", { n: 3 }): the text for the key in the language, with {n} filled in.
// A text can also be a function of the values (plurals, sentences with bold or
// links in them). A key missing in a language shows the English text.
export const translate = (lang, key, vars) => {
  let value = (strings[lang] || strings.en)[key];
  if (value === undefined) {
    value = strings.en[key];
  }
  if (value === undefined) {
    if (process.env.NODE_ENV !== "production") {
      console.warn(`Checkout text missing: ${key}`);
    }
    return key;
  }
  if (typeof value === "function") {
    return value(vars || {});
  }
  if (vars) {
    return value.replace(/\{(\w+)\}/g, (whole, name) => (vars[name] !== undefined && vars[name] !== null ? String(vars[name]) : whole));
  }
  return value;
};

// Dollar amounts: the English page keeps its formats ("$74.85"); the others say
// which dollars they are ("US$74.85"), with the same digits.
export const usd = (text, lang = current) => (isTranslated(lang) && typeof text === "string" ? text.replace(/\$/g, "US$") : text);

const parts = (ms, locale, options) => {
  const out = {};
  new Intl.DateTimeFormat(locale, options).formatToParts(new Date(ms)).forEach((part) => {
    out[part.type] = part.value;
  });
  return out;
};

// "jueves 2 de octubre, 14:00", "quinta-feira, 2 de outubro, 14:00"
export const longDateTime = (ms, lang) => {
  const p = parts(ms, intlLocale(lang), { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
  return `${p.weekday}${lang === "pt" ? "," : ""} ${p.day} de ${p.month}, ${p.hour}:${p.minute}`;
};

// "jue 2 oct", "qui 2 out"
export const shortDay = (ms, lang) => {
  const p = parts(ms, intlLocale(lang), { weekday: "short", day: "numeric", month: "short" });
  return `${p.weekday} ${p.day} ${p.month}`.replace(/\./g, "");
};

// "14:00"
export const clockTime = (ms, lang) => {
  const p = parts(ms, intlLocale(lang), { hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
  return `${p.hour}:${p.minute}`;
};

const TURNAROUND_WORDS = {
  es: { hour: "hora", hours: "horas", days: "días" },
  pt: { hour: "hora", hours: "horas", days: "dias" },
};

// getDue's turnaround ("24 hours", "2-3 days") in the page's language.
export const turnaround = (text, lang = current) => {
  const words = TURNAROUND_WORDS[lang];
  if (!words || typeof text !== "string") {
    return text;
  }
  const hours = text.match(/^(\d+) hours?$/);
  if (hours) {
    return `${hours[1]} ${hours[1] === "1" ? words.hour : words.hours}`;
  }
  const days = text.match(/^(\d+)-(\d+) days?$/);
  if (days) {
    return `${days[1]}-${days[2]} ${words.days}`;
  }
  return text;
};

// Accents and case don't matter when searching a list ("espanol" finds "Español").
export const searchable = (text) => String(text || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

const English = {
  lang: "en",
  chosen: "en",
  onCheckout: false,
  locked: false,
  setLocked: () => {},
  setLang: () => {},
  t: (key, vars) => translate("en", key, vars),
  usd: (text) => text,
};

const CheckoutLanguageContext = createContext(English);

export const CheckoutLanguageProvider = ({ children }) => {
  const location = useLocation();
  const history = useHistory();
  const onCheckout = isCheckoutPath(location.pathname);
  const [chosen, setChosen] = useState(() => {
    const asked = fromUrl(location.search);
    if (asked) {
      store(asked);
      return asked;
    }
    return readStored() || fromBrowser();
  });

  // A link with ?lang= (the Spanish landing page's buttons, for example).
  useEffect(() => {
    const asked = fromUrl(location.search);
    if (asked) {
      store(asked);
      setChosen(asked);
    }
  }, [location.search]);

  const lang = onCheckout ? chosen : "en";
  current = lang;

  // The switch waits while a payment is running (the card form would reload).
  const [locked, setLocked] = useState(false);

  // <html lang> says the checkout's language, so the browser doesn't offer to
  // translate a page that is already in the reader's language. Leaving the
  // checkout puts back what was there.
  const htmlLangBefore = useRef(null);
  useEffect(() => {
    const html = document.documentElement;
    if (onCheckout) {
      if (htmlLangBefore.current === null) {
        htmlLangBefore.current = html.getAttribute("lang") || "en";
      }
      html.setAttribute("lang", lang === "pt" ? "pt-BR" : lang);
    } else if (htmlLangBefore.current !== null) {
      html.setAttribute("lang", htmlLangBefore.current);
      htmlLangBefore.current = null;
    }
  }, [onCheckout, lang]);

  const setLang = useCallback((next) => {
    if (!LANGS.includes(next)) {
      return;
    }
    store(next);
    setChosen(next);
    // A ?lang= left in the address would bring the old language back on reload.
    const params = new URLSearchParams(location.search);
    if (params.has("lang")) {
      params.delete("lang");
      const search = params.toString();
      history.replace({ pathname: location.pathname, search: search ? `?${search}` : "", hash: location.hash });
    }
  }, [history, location.pathname, location.search, location.hash]);

  const value = useMemo(() => ({
    lang,
    chosen,
    onCheckout,
    locked,
    setLocked,
    setLang,
    t: (key, vars) => translate(lang, key, vars),
    usd: (text) => usd(text, lang),
  }), [lang, chosen, onCheckout, locked, setLang]);

  return <CheckoutLanguageContext.Provider value={value}>{children}</CheckoutLanguageContext.Provider>;
};

// { lang, t, usd, setLang, onCheckout }. English outside the checkout.
export const useCheckoutT = () => useContext(CheckoutLanguageContext);
